import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import API from '@/lib/api';

const ProvinceContext = createContext(null);

const PROVINCES = [
  { id: "bruxelles", name: "Bruxelles-Capitale" }, { id: "anvers", name: "Anvers" },
  { id: "brabant-flamand", name: "Brabant flamand" }, { id: "brabant-wallon", name: "Brabant wallon" },
  { id: "flandre-occidentale", name: "Flandre occidentale" }, { id: "flandre-orientale", name: "Flandre orientale" },
  { id: "hainaut", name: "Hainaut" }, { id: "liege", name: "Liège" },
  { id: "limbourg", name: "Limbourg" }, { id: "luxembourg", name: "Luxembourg" }, { id: "namur", name: "Namur" },
];

export function ProvinceProvider({ children }) {
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [regionStatus, setRegionStatus] = useState({});

  useEffect(() => {
    API.get('/regions')
      .then(res => {
        const status = {};
        res.data.forEach(r => { status[r.id] = { active: r.active !== false, message: r.message || '' }; });
        setRegionStatus(status);
      })
      .catch(() => {});
  }, []);

  const selectProvince = useCallback((provinceId) => {
    setSelectedProvince(provinceId);
  }, []);

  const clearProvince = useCallback(() => {
    setSelectedProvince(null);
  }, []);

  const isRegionActive = useCallback((regionId) => {
    return regionStatus[regionId]?.active !== false;
  }, [regionStatus]);

  const getRegionMessage = useCallback((regionId) => {
    return regionStatus[regionId]?.message || '';
  }, [regionStatus]);

  const refreshRegions = useCallback(() => {
    API.get('/regions')
      .then(res => {
        const status = {};
        res.data.forEach(r => { status[r.id] = { active: r.active !== false, message: r.message || '' }; });
        setRegionStatus(status);
      })
      .catch(() => {});
  }, []);

  const provinceName = useMemo(() =>
    selectedProvince
      ? PROVINCES.find(p => p.id === selectedProvince)?.name || selectedProvince
      : null,
    [selectedProvince]
  );

  const contextValue = useMemo(
    () => ({ selectedProvince, provinceName, selectProvince, clearProvince, PROVINCES, isRegionActive, getRegionMessage, regionStatus, refreshRegions }),
    [selectedProvince, provinceName, selectProvince, clearProvince, isRegionActive, getRegionMessage, regionStatus, refreshRegions],
  );

  return (
    <ProvinceContext.Provider value={contextValue}>
      {children}
    </ProvinceContext.Provider>
  );
}

export function useProvince() {
  const ctx = useContext(ProvinceContext);
  if (!ctx) throw new Error('useProvince must be used within ProvinceProvider');
  return ctx;
}
